import { useState, useEffect, useCallback, useRef } from 'react';

interface UseCountdownTimerProps {
  duration: number;
  onComplete?: () => void;
}

export function useCountdownTimer({ duration, onComplete }: UseCountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef<number>();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const startTimer = useCallback(() => {
    setTimeLeft(duration);
    setIsRunning(true);
  }, [duration]);

  const stopTimer = useCallback(() => {
    clearTimer();
    setIsRunning(false);
    setTimeLeft(0);
  }, [clearTimer]);

  useEffect(() => {
    if (!isRunning || timeLeft === 0) return;

    timerRef.current = window.setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearTimer();
          setIsRunning(false);
          onComplete?.();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return clearTimer;
  }, [isRunning, timeLeft, onComplete, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimer;
  }, [clearTimer]);

  return {
    timeLeft,
    isRunning,
    startTimer,
    stopTimer
  };
}